import { Validator } from 'fluentvalidation-ts';
import { UnitExtraPartFormModel, UnitExtraPartFormModelValidator } from './unitExtraPartFormModel';
import { every, some, sumBy, flatMap, uniq } from 'lodash';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { fieldRequiredValidationMessage } from '../../../../validators/sharedValidators';
import {
  beAnInteger,
  beGreaterThanOrEqualTo,
  beLessThanOrEqualTo,
  beNumeric,
  bePositiveNumber,
} from '../../../../validators/stringValidators';
import { PumpFormModel } from '../GetDataForQuoteOrDesignForm';
import { HeatPumpFormModel, HeatPumpFormModelValidator } from '../heat-pump/heatPumpFormModel';
import {
  initialManifoldFormModel,
  ManifoldFormModel,
  ManifoldFormModelValidator,
  manifoldHasSkirtingHeating,
} from '../manifolds/manifoldFormModel';
import {
  FloorLevelCode,
  PumpLocationCode,
  PumpSelectionTypeCode,
  UnitHeatSourceTypeCode,
} from '../quoteOrDesignFormData';
import { QuoteOrDesignFormType } from '../quoteOrDesignFormType';
import { SolarFormModel, SolarFormModelValidator } from '../solar/solarFormModel';
import { parseCylinderVolume } from './cylinderVolumes';
import {
  UnitHeatLossDetailsFormModel,
  UnitHeatLossDetailsFormModelValidator,
} from './heatLoss/unitHeatLossDetailsFormModel';

export type UnitFormModel = {
  unitId: number | null;
  id: string;
  isExpanded: boolean;
  totalUnderfloorHeatingAreaInMetresSquared: number;
  name: string;
  quantity: string;
  numberOfBedrooms: string;
  numberOfBathrooms: string;
  cylinderVolumeOption: string | null;
  hasPipelineMap: boolean;
  pipelineMapJson: string;
  waterBufferSizeInLitres: number | null;
  boilerCapacityInKilowatts: number | null;
  pumpSelectionType: PumpSelectionTypeCode;
  pumpLocation: PumpLocationCode;
  pumps: Array<PumpFormModel | null>;
  underfloorHeatingExtraParts: Array<UnitExtraPartFormModel>;
  heatPumpExtraParts: Array<UnitExtraPartFormModel>;
  heatSourceTypeCode: UnitHeatSourceTypeCode | null;
  heatPump: HeatPumpFormModel | null;
  solar: SolarFormModel | null;
  manifolds: Array<ManifoldFormModel>;
  heatLossDetails: UnitHeatLossDetailsFormModel | null;
  designViewType: DesignFormUnitViewType | null;
};

export type DesignFormUnitViewType = 'Basic' | 'Performance' | 'Thermostats' | 'Cad' | 'Other';

export type DesignFormMode = 'Design' | 'Map';

export const initialUnitId = 'initial-unit';

export const initialUnitFormModel: UnitFormModel = {
  unitId: null,
  id: initialUnitId,
  isExpanded: true,
  totalUnderfloorHeatingAreaInMetresSquared: 0,
  name: 'Unit 1',
  quantity: '1',
  numberOfBedrooms: '0',
  numberOfBathrooms: '0',
  cylinderVolumeOption: null,
  hasPipelineMap: false,
  pipelineMapJson: '',
  waterBufferSizeInLitres: null,
  boilerCapacityInKilowatts: null,
  pumpSelectionType: 'Automatic',
  pumpLocation: 'PumpOnManifold',
  pumps: [null],
  underfloorHeatingExtraParts: [],
  heatPumpExtraParts: [],
  heatSourceTypeCode: 'Boiler',
  heatPump: null,
  solar: null,
  manifolds: [initialManifoldFormModel],
  heatLossDetails: null,
  designViewType: null,
};

export const getDistinctFloorLevelCodes = (unit: UnitFormModel): Array<FloorLevelCode | null> =>
  uniq(
    flatMap(
      unit.manifolds.filter(manifold => manifold.rooms != null),
      manifold => assertNotNull(manifold.rooms).map(room => room.floorLevelCode),
    ),
  );

export class UnitFormModelValidator extends Validator<UnitFormModel> {
  constructor(isDraft: boolean, formType: QuoteOrDesignFormType) {
    super();

    this.ruleFor('quantity')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .must([beNumeric, beAnInteger, bePositiveNumber, beLessThanOrEqualTo(1000)]);

    this.ruleFor('numberOfBedrooms')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft)
      .must(beNumeric)
      .must(beAnInteger)
      .must(beGreaterThanOrEqualTo(0))
      .must(beLessThanOrEqualTo(1000));

    this.ruleFor('numberOfBathrooms')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .unless(() => isDraft)
      .must(beNumeric)
      .must(beAnInteger)
      .must(beGreaterThanOrEqualTo(0))
      .must(beLessThanOrEqualTo(1000));

    if (!isDraft) {
      this.ruleFor('cylinderVolumeOption')
        .notNull()
        .withMessage(fieldRequiredValidationMessage)
        .when(unit => unitHasSolar(unit) || unitHasHeatPump(unit));

      this.ruleFor('cylinderVolumeOption')
        .must(volumeOption => !!volumeOption && parseCylinderVolume(volumeOption) >= 250)
        .withMessage('Must be at least 250 when the Unit includes both Solar and a Heat Pump')
        .when(unit => unitHasSolar(unit) && unitHasHeatPump(unit));

      this.ruleFor('cylinderVolumeOption')
        .must(volumeOption => !!volumeOption && parseCylinderVolume(volumeOption) >= 300)
        .withMessage('Must be at least 300 when the Unit includes Solar but does not include a Heat Pump')
        .when(unit => unitHasSolar(unit) && !unitHasHeatPump(unit));

      this.ruleForEach('pumps')
        .notNull()
        .withMessage(fieldRequiredValidationMessage)
        .when(
          unit =>
            !unit.hasPipelineMap && unit.pumpLocation === 'RemotePump' && unit.pumpSelectionType !== 'PumpsNotSupplied',
        )
        .must(
          (_, unit) =>
            sumBy(unit.pumps, pump => (pump != null ? pump.maxAreaInMetresSquared : 0)) >=
            unit.totalUnderfloorHeatingAreaInMetresSquared,
        )
        .withMessage('Unit area too large for this Pump combination')
        .when(
          unit =>
            !unit.hasPipelineMap &&
            every(unit.pumps, pump => pump != null && pump.partId != null) &&
            unit.pumpSelectionType === 'Automatic',
          'AppliesToCurrentValidator',
        );

      this.ruleFor('pumpLocation')
        .must(pumpLocation => pumpLocation !== 'RemotePump')
        .withMessage(
          `Pump on Manifold must be used when the Unit has ${maximumNumberOfPortsForPumpOnManifold} ports or fewer`,
        )
        .when(
          unit =>
            !unit.hasPipelineMap &&
            unit.pumpSelectionType !== 'PumpsNotSupplied' &&
            unitMustHaveAPumpOnEveryManifold(unit, formType),
        );

      this.ruleFor('heatSourceTypeCode')
        .notNull()
        .withMessage(`Must equal 'Heat Pump' when a Heat Pump is present`)
        .equal('HeatPump')
        .withMessage(`Must equal 'Heat Pump' when a Heat Pump is present`)
        .when(unit => unitHasHeatPump(unit));

      this.ruleFor('heatSourceTypeCode')
        .notNull()
        .withMessage('This field is required when the Unit includes Underfloor Heating')
        .when(unit => unitHasUnderfloorHeating(unit));
    }

    this.ruleFor('name')
      .notEmpty()
      .withMessage('Please enter a name for this Unit')
      .maxLength(100)
      .withMessage('Please enter a name that is no more than 100 characters long');

    this.ruleFor('heatPump').setValidator(() => new HeatPumpFormModelValidator(isDraft));

    this.ruleFor('solar').setValidator(() => new SolarFormModelValidator(isDraft));

    this.ruleForEach('underfloorHeatingExtraParts').setValidator(() => new UnitExtraPartFormModelValidator());
    this.ruleForEach('heatPumpExtraParts').setValidator(() => new UnitExtraPartFormModelValidator());

    this.ruleForEach('manifolds').setValidator(unit => new ManifoldFormModelValidator(isDraft, unit));

    this.ruleFor('heatLossDetails')
      .setValidator(
        (unit: UnitFormModel) =>
          new UnitHeatLossDetailsFormModelValidator(!isDraft && !allRoomsHaveHeatLossOverridden(unit)),
      )
      .when(unit => unit.heatLossDetails != null && unitCanHaveHeatLossInformation(unit));
  }
}

export const unitOnlyHasRoomlessManifolds = (unit: UnitFormModel) =>
  every(unit.manifolds, manifold => manifold.roomlessManifold != null && manifold.rooms == null);

export const unitOnlyHasManifoldsWithRooms = (unit: UnitFormModel) =>
  every(
    unit.manifolds,
    manifold => manifold.rooms != null && manifold.rooms.length > 0 && manifold.roomlessManifold == null,
  );

export const unitHasHeatPumpIfItHasRadiatorManifold = (unit: UnitFormModel) =>
  every(unit.manifolds, manifold => !manifold.isRadiator) || unitHasHeatPump(unit);

export const unitHasAtMostOneRadiatorManifold = (unit: UnitFormModel) =>
  unit.manifolds.filter(manifold => manifold.isRadiator).length <= 1;

export const unitHasManifoldsIfItHasHeatPump = (unit: UnitFormModel) => unit.heatPump == null || some(unit.manifolds);

export const unitHasUnderfloorHeating = (unit: UnitFormModel) => unit.manifolds.filter(m => !m.isRadiator).length > 0;

export const maximumNumberOfPortsForPumpOnManifold = 20;

export const getTotalNumberOfPortsForUnit = (unit: UnitFormModel) =>
  sumBy(unit.manifolds, manifold => Number(manifold.totalNumberOfPorts));

export const unitMustHaveAPumpOnEveryManifold = (unit: UnitFormModel, formType: QuoteOrDesignFormType) => {
  if (formType !== 'Quote' || unit.heatSourceTypeCode === 'HeatPump' || unitHasSkirtingHeating(unit)) {
    return false;
  }

  const totalNumberOfPorts = getTotalNumberOfPortsForUnit(unit);

  return totalNumberOfPorts > 0 && totalNumberOfPorts <= maximumNumberOfPortsForPumpOnManifold;
};

export const unitHasHeatPump = (unit: UnitFormModel) => unit.heatPump != null;

export const unitHasSkirtingHeating = (unit: UnitFormModel) => some(unit.manifolds, manifoldHasSkirtingHeating);

export const unitHasSolar = (unit: UnitFormModel) => unit.solar != null;

export const unitCanHaveHeatLossInformation = (unit: UnitFormModel): boolean =>
  unitHasUnderfloorHeating(unit) || unitHasHeatPump(unit);

const allRoomsHaveHeatLossOverridden = (unit: UnitFormModel): boolean => {
  const rooms = flatMap(unit.manifolds, manifold => manifold.rooms);
  return rooms.every(room => room?.heatLossDetails?.overrideTotalHeatLossInWatts);
};
